import React, { useState } from 'react';
import axios from 'axios';
import { XMarkIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const StockTransferModal = ({ product, onClose, onSuccess }) => {
  const [fromWarehouse, setFromWarehouse] = useState('b2c');
  const [toWarehouse, setToWarehouse] = useState('b2b');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const maxQuantity = fromWarehouse === 'b2c' ? product.stock_b2c : product.stock_b2b;

  const handleWarehouseSwitch = () => {
    const newFrom = fromWarehouse === 'b2c' ? 'b2b' : 'b2c';
    const newTo = toWarehouse === 'b2c' ? 'b2b' : 'b2c';
    setFromWarehouse(newFrom);
    setToWarehouse(newTo);
  };

  const getWarehouseName = (warehouse) => {
    return warehouse === 'b2c' ? 'B2C (קמעונאי)' : 'B2B (סיטונאי)';
  };

  const getWarehouseIcon = (warehouse) => {
    return warehouse === 'b2c' ? '🛒' : '🏢';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const qty = parseInt(quantity);
    if (qty <= 0) {
      toast.error('יש להזין כמות גדולה מ-0');
      return;
    }

    if (qty > maxQuantity) {
      toast.error(`אין מספיק מלאי במחסן ${getWarehouseName(fromWarehouse)}. זמין: ${maxQuantity}`);
      return;
    }

    try {
      setLoading(true);

      await axios.post('/api/inventory/transfer', {
        product_id: product.id,
        from_warehouse: fromWarehouse,
        to_warehouse: toWarehouse,
        quantity: qty,
        notes
      });

      toast.success('המלאי הועבר בהצלחה');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בהעברת המלאי');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">העברת מלאי בין מחסנים</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Product Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-4">
              {product.images && product.images[0] && (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-16 h-16 object-cover rounded"
                />
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900">{product.name}</div>
                <div className="text-sm text-gray-600">{product.sku}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-indigo-50 rounded-lg p-3">
                <div className="text-sm text-indigo-600 font-medium">🛒 מחסן B2C</div>
                <div className="text-2xl font-bold text-indigo-900">{product.stock_b2c || 0}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-sm text-green-600 font-medium">🏢 מחסן B2B</div>
                <div className="text-2xl font-bold text-green-900">{product.stock_b2b || 0}</div>
              </div>
            </div>
          </div>

          {/* Transfer Direction */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 text-center">
                <div className="text-4xl mb-2">{getWarehouseIcon(fromWarehouse)}</div>
                <div className="font-medium text-gray-900">{getWarehouseName(fromWarehouse)}</div>
                <div className="text-3xl font-bold text-gray-700 mt-2">
                  {fromWarehouse === 'b2c' ? product.stock_b2c : product.stock_b2b}
                </div>
                <div className="text-sm text-gray-500">זמין</div>
              </div>

              <div className="px-6">
                <button
                  type="button"
                  onClick={handleWarehouseSwitch}
                  className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                  title="החלף כיוון"
                >
                  <ArrowsRightLeftIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 text-center">
                <div className="text-4xl mb-2">{getWarehouseIcon(toWarehouse)}</div>
                <div className="font-medium text-gray-900">{getWarehouseName(toWarehouse)}</div>
                <div className="text-3xl font-bold text-gray-700 mt-2">
                  {toWarehouse === 'b2c' ? product.stock_b2c : product.stock_b2b}
                </div>
                <div className="text-sm text-gray-500">נוכחי</div>
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              כמות להעברה *
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              min="1"
              max={maxQuantity}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
            <div className="mt-1 text-sm text-gray-500">
              מקסימום: {maxQuantity} יחידות
            </div>
          </div>

          {/* Preview */}
          {quantity > 0 && parseInt(quantity) <= maxQuantity && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm font-medium text-green-900 mb-2">תצוגה מקדימה:</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">🛒 B2C:</div>
                  <div className="font-bold">
                    {product.stock_b2c} → {fromWarehouse === 'b2c' 
                      ? product.stock_b2c - parseInt(quantity)
                      : product.stock_b2c + parseInt(quantity)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600">🏢 B2B:</div>
                  <div className="font-bold">
                    {product.stock_b2b} → {fromWarehouse === 'b2b'
                      ? product.stock_b2b - parseInt(quantity)
                      : product.stock_b2b + parseInt(quantity)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              הערות (אופציונלי)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="סיבת ההעברה, הוראות מיוחדות..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              ביטול
            </button>
            <button
              type="submit"
              disabled={loading || !quantity || parseInt(quantity) > maxQuantity}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'מעביר...' : 'אישור העברה'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockTransferModal;
